'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, LoaderIcon } from './icons';
import { motion, AnimatePresence } from 'framer-motion';
import { Markdown } from './markdown';

interface MessageReasoningProps {
  isLoading: boolean;
  reasoning: string;
}

export function MessageReasoning({
  isLoading,
  reasoning,
}: MessageReasoningProps) {
  // User-controlled expansion for finished reasoning; streaming always forces open
  const [userExpanded, setUserExpanded] = useState(false);
  const isExpanded = isLoading || userExpanded;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const variants = {
    collapsed: {
      height: 0,
      opacity: 0,
      marginTop: 0,
      marginBottom: 0,
    },
    expanded: {
      height: 'auto',
      opacity: 1,
      marginTop: '1rem',
      marginBottom: '0.5rem',
    },
  };

  // Default-collapse when streaming stops; streaming forces open and auto-scrolls
  useEffect(() => {
    if (isLoading) {
      // Ensure we follow new tokens when the panel is open
      const id = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 10);
      return () => clearTimeout(id);
    } else {
      // Return to default collapsed state when not actively reasoning
      setUserExpanded(false);
    }
  }, [isLoading]);

  // Auto-scroll as content streams, unless the user scrolled up
  useEffect(() => {
    if (isLoading && isExpanded && isAtBottom && containerRef.current) {
      const id = setTimeout(() => {
        const el = containerRef.current!;
        el.scrollTop = el.scrollHeight;
      }, 10);
      return () => clearTimeout(id);
    }
  }, [reasoning, isLoading, isExpanded, isAtBottom]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const threshold = 24; // px
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    setIsAtBottom(atBottom);
  };

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <div className="flex flex-row gap-2 items-center">
          <div className="font-medium">Reasoning</div>
          <div className="animate-spin">
            <LoaderIcon />
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-2 items-center">
          <div className="font-medium">Reasoned for a few seconds</div>
          <button
            data-testid="message-reasoning-toggle"
            type="button"
            className="cursor-pointer"
            onClick={() => {
              setUserExpanded((v) => !v);
            }}
          >
            <span className={`transition-transform ${isExpanded ? '' : 'rotate-180'}`}>
              <ChevronDownIcon />
            </span>
          </button>
        </div>
      )}

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            data-testid="message-reasoning"
            key="content"
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            variants={variants}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
            className="pl-4 text-zinc-600 dark:text-zinc-400 border-l flex flex-col gap-4"
          >
            <div
              ref={containerRef}
              className="max-h-64 overflow-y-auto pr-1"
              onScroll={handleScroll}
            >
              <Markdown>{reasoning}</Markdown>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
